// Canonical DM-unread peer id for a live agent session (#1185 follow-up).
// Extracted from app.svelte.ts so the mapping is testable under plain-node
// vitest — app.svelte.ts can't be imported there (Svelte runes), same reason
// unread-reconcile.ts was split out.
//
// THE BUG: a cybo's live agent session id is a fresh UUID per summon, but the
// server attributes its DM unread (increment + the dm_reads cursor heal) to
// the STABLE cybo id — `fromId = cyboId ?? agentId` (message-router.ts
// handleAgentMessage, #1185). The URL and the agents-list row still carry the
// raw per-session id, so a LOCAL clear/active-check against that raw id
// silently misses the entry the server actually wrote: the rail "Chat" badge
// and the macOS dock badge (both plain sums over notificationState's counts
// map) only self-correct on the next full fetch_unread reseed instead of
// clearing the moment the DM is opened.
export interface AgentSessionRow {
  agentId: string;
  cyboId?: string | null;
}

// Resolves `agentId` (a live session id, e.g. from the URL or a dm_broadcast)
// to the id the server keys DM unread on: the session's cybo id when it's
// cybo-bound, else the raw agentId unchanged. Mirrors the server's own
// `cyboId ?? agentId` fallback, so a client-side compare/clear always targets
// the SAME key the server incremented/healed. Also falls back to the raw id
// when no matching row is found (agents list not loaded yet) — a miss here
// must never throw or drop the clear.
export function canonicalAgentDmPeerId(
  agents: readonly AgentSessionRow[],
  agentId: string,
): string {
  return cyboIdsOf(agents).get(agentId) ?? agentId;
}

// One map per roster array: rosters are replaced on write, except removeCyboSessions (forgetRoster).
const cyboIdsByRoster = new WeakMap<readonly unknown[], Map<string, string | undefined>>();
function cyboIdsOf(agents: readonly AgentSessionRow[]): Map<string, string | undefined> {
  let ids = cyboIdsByRoster.get(agents);
  if (!ids) {
    ids = new Map();
    for (const { agentId, cyboId } of agents)
      if (!ids.has(agentId)) ids.set(agentId, cyboId ?? undefined); // first row wins, as find() did
    cyboIdsByRoster.set(agents, ids);
  }
  return ids;
}

export function forgetRoster(agents: readonly unknown[]): void {
  cyboIdsByRoster.delete(agents);
}
